import { useState, useEffect, useCallback, useMemo } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCurrentShop } from "@/lib/auth";
import { getMovements, getMovementStats } from "@/lib/api";
import {
  Package,
  RefreshCw,
  Plus,
  Minus,
  AlertTriangle,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { EntryModal } from "./ShopInventoryMovements/EntryModal";
import { ExitModal } from "./ShopInventoryMovements/ExitModal";
import { AdjustmentModal } from "./ShopInventoryMovements/AdjustmentModal";
import { DetailModal } from "./ShopInventoryMovements/DetailModal";

const TYPE_CONFIG: Record<string, { label: string; icon: string; className: string }> = {
  entry: { label: "Entrada", icon: "📥", className: "text-green-600 bg-green-50" },
  exit: { label: "Salida", icon: "📤", className: "text-red-600 bg-red-50" },
  waste: { label: "Merma", icon: "🗑️", className: "text-orange-600 bg-orange-50" },
  adjustment: { label: "Ajuste", icon: "🟡", className: "text-yellow-600 bg-yellow-50" },
};

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  confirmed: { label: "Confirmada", variant: "default" },
  draft: { label: "Borrador", variant: "secondary" },
  cancelled: { label: "Cancelada", variant: "destructive" },
};

export default function ShopInventoryMovements() {
  const currentShop = getCurrentShop()!;

  // List state
  const [movements, setMovements] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Stats state
  const [stats, setStats] = useState({
    totalEntries: 0, totalExits: 0, totalWaste: 0,
    entriesQuantity: 0, exitsQuantity: 0,
    entriesCost: 0, exitsCost: 0, wasteCost: 0,
  });

  // Filters
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const limit = 20;

  // Modals
  const [showEntry, setShowEntry] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const monthStart = useMemo(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
    [],
  );
  const monthEnd = useMemo(() => new Date().toISOString(), []);

  const loadStats = useCallback(async () => {
    try {
      const data = await getMovementStats(currentShop.id, {
        startDate: monthStart,
        endDate: monthEnd,
      });
      setStats(data);
    } catch {}
  }, [currentShop.id, monthStart, monthEnd]);

  const loadMovements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMovements(currentShop.id, {
        page,
        limit,
        search: search || undefined,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        startDate: monthStart,
        endDate: monthEnd,
      });
      setMovements(data.movements);
      setTotal(data.total);
    } catch {
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [currentShop.id, page, search, typeFilter, statusFilter, monthStart, monthEnd]);

  useEffect(() => {
    loadMovements();
    loadStats();
  }, [loadMovements, loadStats]);

  const handleSuccess = () => {
    loadMovements();
    loadStats();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <PageContainer className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold uppercase">
          🚚 Entradas/Salidas
        </h1>
        <p className="text-muted-foreground mt-2">
          Gestiona los movimientos de inventario — {currentShop.name}
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📥</div>
              <div>
                <p className="text-2xl font-bold text-green-600">+{stats.entriesQuantity}</p>
                <p className="text-sm text-muted-foreground">Entradas este mes</p>
                <p className="text-xs text-muted-foreground">
                  ${stats.entriesCost.toFixed(2)} en costo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="text-3xl">📤</div>
              <div>
                <p className="text-2xl font-bold text-red-600">-{stats.exitsQuantity}</p>
                <p className="text-sm text-muted-foreground">Salidas este mes</p>
                {stats.totalWaste > 0 && (
                  <p className="text-xs text-orange-600">
                    Incluye {stats.totalWaste} merma(s) — ${stats.wasteCost.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="text-3xl">💰</div>
              <div>
                <p className="text-2xl font-bold">${(stats.entriesCost - stats.exitsCost).toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">Neto del mes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => setShowEntry(true)}>
          <Plus className="mr-2 h-4 w-4" /> Nueva Entrada
        </Button>
        <Button variant="secondary" onClick={() => setShowExit(true)}>
          <Minus className="mr-2 h-4 w-4" /> Nueva Salida
        </Button>
        <Button variant="outline" onClick={() => setShowAdjustment(true)}>
          <AlertTriangle className="mr-2 h-4 w-4" /> Ajuste
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            placeholder="Buscar por nº o referencia..."
          />
        </div>
        <div className="w-[150px]">
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Todos los tipos</SelectItem>
              <SelectItem value="entry">Entradas</SelectItem>
              <SelectItem value="exit">Salidas</SelectItem>
              <SelectItem value="waste">Mermas</SelectItem>
              <SelectItem value="adjustment">Ajustes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[150px]">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Todos los estados</SelectItem>
              <SelectItem value="confirmed">Confirmadas</SelectItem>
              <SelectItem value="draft">Borradores</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(search || typeFilter || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setTypeFilter(""); setStatusFilter(""); setPage(1); }}>
            ✕ Limpiar
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-left font-medium">Nº</th>
                  <th className="p-3 text-left font-medium">Fecha</th>
                  <th className="p-3 text-left font-medium">Tipo</th>
                  <th className="p-3 text-left font-medium">Productos</th>
                  <th className="p-3 text-right font-medium">Cant</th>
                  <th className="p-3 text-right font-medium">Valor</th>
                  <th className="p-3 text-center font-medium">Estado</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    </td>
                  </tr>
                ) : movements.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      No hay movimientos registrados
                    </td>
                  </tr>
                ) : (
                  movements.map((m) => {
                    const tc = TYPE_CONFIG[m.type] || { label: m.type, icon: "📦", className: "" };
                    const sb = STATUS_BADGE[m.status] || { label: m.status, variant: "outline" };
                    return (
                      <tr
                        key={m.id}
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => setDetailId(m.id)}
                      >
                        <td className="p-3 font-mono text-xs">{m.sequential}</td>
                        <td className="p-3 text-muted-foreground">
                          {m.createdAt ? new Date(m.createdAt).toLocaleDateString("es-CU") : "—"}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${tc.className}`}>
                            {tc.icon} {tc.label}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {m.lines?.length || "—"} producto(s)
                        </td>
                        <td className="p-3 text-right font-medium">
                          {m.totalQuantity}
                        </td>
                        <td className="p-3 text-right font-medium">
                          ${parseFloat(m.totalCost || "0").toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant={sb.variant}>{sb.label}</Badge>
                        </td>
                        <td className="p-3 text-right text-muted-foreground">👁️</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .map((p, idx, arr) => (
              <span key={p} className="flex items-center">
                {idx > 0 && arr[idx - 1] !== p - 1 && <span className="mx-1 text-muted-foreground">...</span>}
                <Button
                  variant={p === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(p)}
                  className="min-w-[32px]"
                >
                  {p}
                </Button>
              </span>
            ))}
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Modals */}
      <EntryModal
        show={showEntry}
        onClose={() => setShowEntry(false)}
        onSuccess={handleSuccess}
      />
      <ExitModal
        show={showExit}
        onClose={() => setShowExit(false)}
        onSuccess={handleSuccess}
      />
      <AdjustmentModal
        show={showAdjustment}
        onClose={() => setShowAdjustment(false)}
        onSuccess={handleSuccess}
      />
      <DetailModal
        movementId={detailId}
        show={detailId !== null}
        onClose={() => setDetailId(null)}
        onUpdate={handleSuccess}
      />
    </PageContainer>
  );
}
